import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/context/LanguageContext';
import { Download, Smartphone, Share, Plus, MoreVertical, ChevronDown, ShoppingBag } from 'lucide-react';

const InstallPWA = () => {
  const { isSpanish } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
    const userAgent = navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    setIsAndroid(/android/.test(userAgent));
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else {
      setShowInstallDialog(true);
    }
  };

  // Detect native wrappers (WebToNative for Google Play / Amazon Appstore,
  // iOS home-screen install) — the user is already inside the installed app,
  // so the "Install" button is redundant AND on small screens it shoves the
  // hamburger off the row, making the menu visually unreachable.
  // Detection signals:
  //   • PWA standalone (already captured)
  //   • iOS home-screen PWA (already captured via navigator.standalone)
  //   • Android WebView (WebToNative + Amazon wrappers all render in a wv)
  //   • window.WTN injection from WebToNative
  //   • Persisted ?fromApp=1 query (set the first time the wrapper opens)
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  const isAndroidWebView = / wv\)/i.test(ua);
  const isWebToNative = typeof window !== 'undefined' && (
    !!window.WTN || !!window.WTNAppData ||
    /webtonative|wtnapp|com\.clearcommunicationapp/i.test(ua)
  );
  let fromAppFlag = false;
  try {
    fromAppFlag = (typeof window !== 'undefined') && (
      new URLSearchParams(window.location.search).get('fromApp') === '1' ||
      localStorage.getItem('cca_from_app') === '1'
    );
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('fromApp') === '1') {
      localStorage.setItem('cca_from_app', '1');
    }
  } catch { /* ignore */ }
  if (isStandalone || isAndroidWebView || isWebToNative || fromAppFlag) return null;

  // Shared "How to install" walkthrough modal — rendered for both the
  // iOS button (single-action) and the desktop / Android dropdown's
  // "Install as PWA" item when the native `beforeinstallprompt` isn't
  // available (e.g. Firefox, Safari).
  const installDialog = (
    <Dialog open={showInstallDialog} onOpenChange={setShowInstallDialog}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            {isSpanish ? 'Instalar ClearCommunicationApp' : 'Install ClearCommunicationApp'}
          </DialogTitle>
          <DialogDescription>
            {isSpanish
              ? 'Añade esta app a tu pantalla de inicio para acceso rápido'
              : 'Add this app to your home screen for quick access'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {isIOS ? (
            <ol className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">1</span>
                <span className="flex items-center gap-1">
                  {isSpanish ? 'Toca el botón' : 'Tap the'} <Share className="h-4 w-4 inline" /> {isSpanish ? 'Compartir' : 'Share button'}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">2</span>
                <span className="flex items-center gap-1">
                  {isSpanish ? 'Desplázate y toca' : 'Scroll and tap'} "<Plus className="h-3 w-3 inline" /> {isSpanish ? 'Añadir a pantalla de inicio' : 'Add to Home Screen'}"
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">3</span>
                <span>{isSpanish ? 'Toca "Añadir"' : 'Tap "Add"'}</span>
              </li>
            </ol>
          ) : isAndroid ? (
            <ol className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">1</span>
                <span className="flex items-center gap-1">
                  {isSpanish ? 'Toca' : 'Tap'} <MoreVertical className="h-4 w-4 inline" /> {isSpanish ? 'menú (tres puntos)' : 'menu (three dots)'}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">2</span>
                <span>{isSpanish ? 'Toca "Instalar app" o "Añadir a pantalla de inicio"' : 'Tap "Install app" or "Add to Home screen"'}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">3</span>
                <span>{isSpanish ? 'Confirma la instalación' : 'Confirm installation'}</span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">1</span>
                <span>{isSpanish ? 'Busca el icono de instalación en la barra de direcciones' : 'Look for install icon in the address bar'}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">2</span>
                <span>{isSpanish ? 'O usa el menú del navegador → "Instalar..."' : 'Or use browser menu → "Install..."'}</span>
              </li>
            </ol>
          )}
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              {isSpanish
                ? '✨ La app funcionará sin conexión y se abrirá como una app nativa'
                : '✨ The app will work offline and open like a native app'}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // iOS: Apple guideline 2.3.3 forbids linking to other Android app
  // stores from inside the iOS in-app browser experience, so we keep the
  // single Install button on iOS and only open the AddToHomeScreen
  // walkthrough.
  if (isIOS) {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={handleInstallClick}
          className="gap-1 px-2 sm:px-3 h-8 sm:h-9 bg-[#00E5CC] border-2 border-[#00BFA5] text-[#0d1f2d] dark:text-[#0d1f2d] hover:bg-[#00D4BD] hover:border-[#00A896] hover:text-[#0d1f2d] dark:hover:text-[#0d1f2d] font-bold shadow-md focus:outline-none focus-visible:ring-0"
          data-testid="install-app-btn"
        >
          <Download className="h-3 w-3 sm:h-4 sm:w-4" />
          <span className="text-[10px] sm:text-sm">
            {isSpanish ? 'Instalar' : 'Install'}
          </span>
        </Button>
        {installDialog}
      </>
    );
  }

  // Android / desktop: dropdown with PWA + Amazon options
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            // Explicit dark-mode text override — without `dark:text-...`
            // shadcn's default `text-foreground` makes the text WHITE in
            // dark mode, which disappears against the teal background.
            // Locked to navy in BOTH modes per user feedback May 2026
            // (this is the third time we've fixed this regression —
            // adding belt-and-suspenders inline comment so future
            // refactors don't strip it again).
            className="gap-1 px-2 sm:px-3 h-8 sm:h-9 bg-[#00E5CC] border-2 border-[#00BFA5] text-[#0d1f2d] dark:text-[#0d1f2d] hover:bg-[#00D4BD] hover:border-[#00A896] hover:text-[#0d1f2d] dark:hover:text-[#0d1f2d] font-bold shadow-md focus:outline-none focus-visible:ring-0"
            data-testid="install-app-btn"
          >
            <Download className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="text-[10px] sm:text-sm">
              {isSpanish ? 'Instalar' : 'Install'}
            </span>
            <ChevronDown className="h-3 w-3 opacity-80" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
            {isSpanish ? 'Elige cómo instalar' : 'Choose how to install'}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleInstallClick}
            className="cursor-pointer"
            data-testid="install-pwa-option"
          >
            <Smartphone className="mr-2 h-4 w-4 text-[#00A896]" />
            <div className="flex flex-col items-start">
              <span className="font-medium">
                {isSpanish ? 'Instalar como PWA' : 'Install as PWA'}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {isSpanish ? 'Rápido · sin tienda de apps' : 'Fast · no app store needed'}
              </span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => window.open('https://play.google.com/store/apps/details?id=www.ClearCommunicationApp.com', '_blank', 'noopener,noreferrer')}
            className="cursor-pointer"
            data-testid="install-google-play-option"
          >
            <svg className="mr-2 h-4 w-4 text-[#01875F]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M3.609 1.814 13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893 2.302 2.302-10.937 6.333 8.635-8.635zM21.61 11.13a1 1 0 0 1 0 1.74l-2.703 1.566-2.508-2.508 2.508-2.508 2.703 1.566zM5.864 2.485l10.937 6.333-2.302 2.302-8.635-8.635z"/>
            </svg>
            <div className="flex flex-col items-start">
              <span className="font-medium">
                {isSpanish ? 'Descargar en Google Play' : 'Download on Google Play'}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {isSpanish ? 'Android oficial' : 'Official Android app'}
              </span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => window.open('https://www.amazon.com/gp/product/B0GX2ZJQGV', '_blank', 'noopener,noreferrer')}
            className="cursor-pointer"
            data-testid="install-amazon-option"
          >
            <ShoppingBag className="mr-2 h-4 w-4 text-[#FF9900]" />
            <div className="flex flex-col items-start">
              <span className="font-medium">
                {isSpanish ? 'Descargar de Amazon' : 'Download on Amazon'}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {isSpanish ? 'Tabletas Fire y más' : 'Fire tablets & more'}
              </span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {installDialog}
    </>
  );
};

export default InstallPWA;
